import type { FormativeHomeCard } from '../../tutor/api/malayFormativeProgress.client';
import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';
import type { AssignmentDashboardStats } from '../hooks/useAssignmentDashboardStats';
import {
  activityTitleLabelKey,
  isMalayFormativeActivityKey,
} from '../../tutor/malayFormativeHomeCards';
import { isPastOrPresentTimestamp, parseTimestamp } from './dashboardDateUtils';

export type MalayRecentActivityType =
  | 'malay_unit'
  | 'malay_reading'
  | 'malay_narrative'
  | 'malay_narrative_draft'
  | 'tutor'
  | 'homework';

export type MalayRecentActivityItem = {
  id: string;
  type: MalayRecentActivityType;
  title: string;
  at: Date;
};

function parseActivityTimestamp(iso: string | null | undefined): Date | null {
  if (!isPastOrPresentTimestamp(iso)) return null;
  return parseTimestamp(iso);
}

function resolveCardTitle(
  card: FormativeHomeCard,
  t: (key: string) => string,
): string {
  const labelKey = activityTitleLabelKey(card.activity_key);
  if (labelKey) return t(labelKey);
  return card.activity_key;
}

/** Build Malay recent-learning timeline from real timestamps only. Max 4 items. */
export function buildMalayRecentActivities(params: {
  formativeCards: FormativeHomeCard[];
  tutorAnalytics: TutorAnalyticsSummary | null;
  assignmentStats: AssignmentDashboardStats;
  genericTutorSessionLabel: string;
  t: (key: string, opts?: Record<string, unknown>) => string;
  maxItems?: number;
}): MalayRecentActivityItem[] {
  const maxItems = params.maxItems ?? 4;
  const items: MalayRecentActivityItem[] = [];

  for (const card of params.formativeCards) {
    if (!isMalayFormativeActivityKey(card.activity_key)) continue;

    const title = resolveCardTitle(card, params.t);

    if (card.activity_key === 'malay_narrative_learning_tutor') {
      const lastActivity = parseActivityTimestamp(card.progress?.last_activity_at);
      const lastSaved = parseActivityTimestamp(card.narrative_writing?.last_saved_at ?? undefined);

      if (lastActivity) {
        items.push({
          id: `malay-narrative-${lastActivity.toISOString()}`,
          type: 'malay_narrative',
          title,
          at: lastActivity,
        });
      }
      if (
        lastSaved &&
        (!lastActivity || lastSaved.getTime() !== lastActivity.getTime())
      ) {
        items.push({
          id: `malay-narrative-draft-${lastSaved.toISOString()}`,
          type: 'malay_narrative_draft',
          title: params.t('dashboard.studentHome.malayRecent.narrativeDraft', { activity: title }),
          at: lastSaved,
        });
      }
      continue;
    }

    const at = parseActivityTimestamp(card.progress?.last_activity_at);
    if (!at) continue;

    const type: MalayRecentActivityType =
      card.activity_key === 'malay_jendela_seni_reading' ? 'malay_reading' : 'malay_unit';

    items.push({
      id: `malay-formative-${card.activity_key}-${at.toISOString()}`,
      type,
      title,
      at,
    });
  }

  for (const session of params.tutorAnalytics?.session_history ?? []) {
    const at = parseActivityTimestamp(session.ended_at || session.started_at);
    if (!at) continue;
    items.push({
      id: `tutor-${session.session_id}`,
      type: 'tutor',
      title: session.topic_label?.trim() || params.genericTutorSessionLabel,
      at,
    });
  }

  for (const fb of params.assignmentStats.recentFeedback) {
    const at = parseActivityTimestamp(fb.submittedAt ?? undefined);
    if (!at) continue;
    items.push({
      id: `hw-${fb.title}-${fb.submittedAt}`,
      type: 'homework',
      title: params.t('dashboard.studentHome.malayRecent.assignmentScore', {
        title: fb.title,
        grade: fb.grade,
      }),
      at,
    });
  }

  return items
    .sort((a, b) => b.at.getTime() - a.at.getTime())
    .slice(0, maxItems);
}
